import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * Retrieves a single entity
 */
export const executeGet = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const canGet = collection.plugins.apiExecutionPlugin.canExecute('get')
    const canBatchGet =
      collection.plugins.apiExecutionPlugin.canExecute('batchGet')
    if (!canGet && !canBatchGet) {
      // TODO: create effect to automatically dispatch cancel on request
      return undefined
    }
    return feature.actions.get.relay((forwardTo, { id }) =>
      rxjs
        .concat(
          // Checking if we should use cache instead of API based on the
          // provided configuration
          collection.plugins.dataOperationPlugin.config.useCacheInsteadOfApi ===
            false
            ? // If not, we can immediately return an empty observable
              rxjs.EMPTY
            : // If yes, we need to check if the entity is already in the cache and return it
              collection.plugins.cachePlugin.feature.selectors
                .getCachedEntity(id)
                .asObservable.pipe(
                  rxjs.take(1),
                  rxjs.filter(TypeChecker.isNotNullish.raw),
                  // If there is a custom logic to decide whether to use cache
                  // or API, we need to check it, if not, we can return the
                  // entity
                  rxjs.filter(
                    entity =>
                      !TypeChecker.isFunction(
                        collection.plugins.dataOperationPlugin.config
                          .useCacheInsteadOfApi,
                      ) ||
                      collection.plugins.dataOperationPlugin.config.useCacheInsteadOfApi(
                        entity,
                      ),
                  ),
                  rxjs.map(entity => ({ success: { response: { entity } } })),
                ),

          // Otherwise, we need to retrieve it
          collection.feature.selectors.context.asObservable.pipe(
            // We need to get the context, but just the most recent value
            rxjs.take(1),
            rxjs.map(context =>
              forwardTo(
                collection.plugins.apiExecutionPlugin.feature.actions.execute,
                {
                  input: canGet
                    ? {
                        args: [id],
                        context,
                        methodName: 'get',
                      }
                    : {
                        args: [[id]],
                        context,
                        methodName: 'batchGet',
                      },
                  output: ({ request: { methodName }, response }) => ({
                    // NOTE: since `execute` can return a variety of
                    // results, we need to tell TypeScript which result we
                    // expect
                    entity:
                      methodName === 'get'
                        ? (response as rxjs.ObservedValueOf<
                            ecModels.Api.Method.Result<
                              'get',
                              typeof collection.api
                            >
                          >)
                        : (
                            response as rxjs.ObservedValueOf<
                              ecModels.Api.Method.Result<
                                'batchGet',
                                typeof collection.api
                              >
                            >
                          )[0],
                  }),
                },
              ),
            ),
          ),
        )
        // We take the first instruction received
        .pipe(rxjs.take(1)),
    ).asObservable
  },
)
